"use client";

import { SpeakingDemoIframe } from "@/components/seo/SpeakingDemoIframe";
import {
  ACQUISITION_DEMO_EVENTS,
  type AcquisitionDemoAnalyticsContext,
  trackAcquisitionDemoEvent,
} from "@/lib/analytics/acquisition-demo-events";
import {
  ACQUISITION_LIVE_DEMO_EXPERIMENT_KEY,
  ACQUISITION_LIVE_DEMO_VARIANTS,
  assignAcquisitionLiveDemoVariant,
  getAcquisitionLiveDemoBucket,
} from "@/lib/experiments/acquisitionLiveDemo";
import { Mic, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useRef, useState } from "react";

const ACQUISITION_DEMO_SEED_KEY = "chickytutor_acquisition_demo_seed";
let sessionFallbackExperimentSeed: string | null = null;

export type AcquisitionLiveDemoConfig = {
  defaultInstructionLanguageCode?: string;
  defaultTargetLanguageCode?: string;
  pageFamily: string;
  pagePath: string;
  placement?: string;
  scenarioId?: string;
};

function getOrCreateExperimentSeed(): string {
  try {
    const savedSeed = window.localStorage.getItem(ACQUISITION_DEMO_SEED_KEY);
    if (savedSeed) return savedSeed;

    const seed = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    window.localStorage.setItem(ACQUISITION_DEMO_SEED_KEY, seed);
    return seed;
  } catch {
    sessionFallbackExperimentSeed ??= `${Date.now()}-${Math.random()}`;
    return sessionFallbackExperimentSeed;
  }
}

export function AcquisitionLiveDemo({
  defaultInstructionLanguageCode,
  defaultTargetLanguageCode,
  pageFamily,
  pagePath,
  placement = "below_product_demo",
  scenarioId,
}: AcquisitionLiveDemoConfig) {
  const posthog = usePostHog();
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedAssignmentRef = useRef(false);
  const hasTrackedLoadRef = useRef(false);
  const hasTrackedViewRef = useRef(false);
  const viewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [variant, setVariant] = useState<"pending" | "control" | "live_demo">("pending");
  const [assignmentId, setAssignmentId] = useState<string>();
  const [shouldLoadEmbed, setShouldLoadEmbed] = useState(false);

  const rolloutPercent = Number(
    process.env.NEXT_PUBLIC_ACQUISITION_LIVE_DEMO_ROLLOUT_PERCENT ||
      (process.env.NODE_ENV === "production" ? "10" : "100")
  );
  const demoFlag = process.env.NEXT_PUBLIC_ACQUISITION_LIVE_DEMOS_ENABLED;
  const demosEnabled =
    demoFlag === "true" || (demoFlag !== "false" && process.env.NODE_ENV !== "production");

  useEffect(() => {
    if (!demosEnabled) {
      setVariant("control");
      return;
    }

    const seed = getOrCreateExperimentSeed();
    setAssignmentId(`bucket-${getAcquisitionLiveDemoBucket(seed)}`);
    setVariant(
      assignAcquisitionLiveDemoVariant({
        seed,
        liveDemoPercent: Number.isFinite(rolloutPercent) ? rolloutPercent : 100,
      })
    );
  }, [demosEnabled, rolloutPercent]);

  const analyticsContext = useMemo<AcquisitionDemoAnalyticsContext>(
    () => ({
      demoId: "acquisition-speaking-demo",
      demoKind: "live_speaking",
      pagePath,
      pageFamily,
      placement,
      experimentKey: ACQUISITION_LIVE_DEMO_EXPERIMENT_KEY,
      experimentVariant: variant === "pending" ? undefined : variant,
      instructionLanguageCode: defaultInstructionLanguageCode,
      languageLevel: "beginner",
      targetLanguageCode: defaultTargetLanguageCode,
      assignmentId,
    }),
    [
      defaultInstructionLanguageCode,
      defaultTargetLanguageCode,
      pageFamily,
      pagePath,
      placement,
      variant,
      assignmentId,
    ]
  );

  useEffect(() => {
    if (
      !demosEnabled ||
      variant === "pending" ||
      !assignmentId ||
      hasTrackedAssignmentRef.current
    ) {
      return;
    }

    hasTrackedAssignmentRef.current = true;
    trackAcquisitionDemoEvent(posthog, ACQUISITION_DEMO_EVENTS.assigned, analyticsContext);
  }, [analyticsContext, assignmentId, demosEnabled, posthog, variant]);

  useEffect(() => {
    const section = sectionRef.current;
    if (
      variant !== ACQUISITION_LIVE_DEMO_VARIANTS.liveDemo ||
      !section ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadEmbed(true);
        observer.disconnect();
      },
      { rootMargin: "500px 0px", threshold: 0.01 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [variant]);

  useEffect(() => {
    const section = sectionRef.current;
    if (
      variant !== ACQUISITION_LIVE_DEMO_VARIANTS.liveDemo ||
      !section ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }

    const clearViewTimer = () => {
      if (!viewTimerRef.current) return;
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry || hasTrackedViewRef.current) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          viewTimerRef.current ??= setTimeout(() => {
            hasTrackedViewRef.current = true;
            viewTimerRef.current = null;
            trackAcquisitionDemoEvent(posthog, ACQUISITION_DEMO_EVENTS.viewed, analyticsContext);
            observer.disconnect();
          }, 1000);
          return;
        }
        clearViewTimer();
      },
      { threshold: 0.5 }
    );
    observer.observe(section);
    return () => {
      clearViewTimer();
      observer.disconnect();
    };
  }, [analyticsContext, posthog, variant]);

  if (!demosEnabled) return null;

  return (
    <section
      ref={sectionRef}
      id="live-speaking-demo"
      aria-labelledby="live-speaking-demo-title"
      className="border-b border-white/10 bg-[#121219]"
    >
      <div className="mx-auto max-w-6xl px-6 py-14 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-300/25 bg-orange-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-orange-200">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {variant === "control" ? "Speaking practice" : "Live speaking demo"}
          </div>
          <h2 id="live-speaking-demo-title" className="mt-4 text-3xl font-semibold md:text-4xl">
            Try ChickyTutor in your language
          </h2>
          <p className="mt-4 leading-7 text-white/70">
            Choose any practice language and any instruction language. Nothing uses your microphone
            or starts a session until you press Start Speaking Practice.
          </p>
        </div>

        <div className="mt-9 overflow-hidden rounded-2xl border border-white/10 bg-[#0f0f14] shadow-2xl shadow-black/30">
          {variant === "pending" ? (
            <div
              className="h-[720px] animate-pulse bg-white/[0.03] sm:h-[680px]"
              aria-label="Preparing speaking practice"
            />
          ) : variant === ACQUISITION_LIVE_DEMO_VARIANTS.control ? (
            <div className="grid h-[720px] place-items-center px-6 py-14 text-center sm:h-[680px]">
              <div className="max-w-lg">
                <Mic className="mx-auto h-10 w-10 text-orange-300" aria-hidden="true" />
                <p className="mt-4 text-2xl font-semibold text-white">Ready to start speaking?</p>
                <p className="mt-3 leading-7 text-white/65">
                  Open the full ChickyTutor experience and choose any practice and instruction
                  language.
                </p>
                <Link
                  href="/"
                  className="mt-6 inline-flex min-h-11 items-center rounded-lg bg-orange-500 px-5 text-sm font-bold text-black transition hover:bg-orange-400"
                >
                  Start Speaking Practice
                </Link>
              </div>
            </div>
          ) : shouldLoadEmbed ? (
            <SpeakingDemoIframe
              defaultInstructionLanguageCode={defaultInstructionLanguageCode}
              defaultTargetLanguageCode={defaultTargetLanguageCode}
              experimentVariant={variant}
              pageFamily={pageFamily}
              pagePath={pagePath}
              placement={placement}
              scenarioId={scenarioId}
              loading="lazy"
              onLoad={() => {
                if (hasTrackedLoadRef.current) return;
                hasTrackedLoadRef.current = true;
                trackAcquisitionDemoEvent(
                  posthog,
                  ACQUISITION_DEMO_EVENTS.loaded,
                  analyticsContext
                );
              }}
            />
          ) : (
            <div className="grid h-[720px] place-items-center px-6 py-14 text-center sm:h-[680px]">
              <div>
                <Mic className="mx-auto h-10 w-10 text-orange-300" aria-hidden="true" />
                <p className="mt-4 text-lg font-semibold text-white">Ready when you are</p>
                <button
                  type="button"
                  onClick={() => setShouldLoadEmbed(true)}
                  className="mt-5 min-h-11 rounded-lg bg-orange-500 px-5 text-sm font-bold text-black transition hover:bg-orange-400"
                >
                  Load live demo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
